import { ActionMenu } from '@components/ActionMenu';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Delete, Edit } from '@mui/icons-material';
import { ListItemIcon, MenuItem } from '@mui/material';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { ExpenseFragmentFragment, useBudgetItemsQuery, useDeleteExpenseMutation, useExpensesQuery, useGetProjectBudgetQuery } from 'gql/index';
import React from 'react';
import { useIntl } from 'react-intl';
import { useBudgetTableContext } from '../BudgetItemTable/context/useBudgetTableContext';

interface Props {
  expense: ExpenseFragmentFragment;
  costItemId: number;
  size?: 'small' | 'medium';
  onEdit?: (expense: ExpenseFragmentFragment, costItemId: number) => void;
}

export const ExpenseActions: React.FC<Props> = ({ expense, costItemId, size, onEdit }) => {
  const { formatMessage } = useIntl();
  const { projectId, canManageBudgetAndExpenses } = useCurrentProject();
  const { notifySuccess } = useNotification();
  const invalidateQuery = useQueryInvalidator();
  const { setEntryDialog } = useBudgetTableContext();

  const [isDeleting, setIsDeleting] = React.useState(false);

  const { mutate: deleteExpense, isPending: isLoading } = useDeleteExpenseMutation({
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Expense deleted successfully' }));
      invalidateQuery(useExpensesQuery, { projectId });
      invalidateQuery(useGetProjectBudgetQuery, { id: projectId });
      invalidateQuery(useBudgetItemsQuery, { projectId });
    }
  });

  const onDelete = () => {
    deleteExpense({
      input: {
        projectId,
        id: expense.id,
      }
    });
  };

  if (!canManageBudgetAndExpenses) {
    return null;
  }

  return <>
    <ActionMenu buttonProps={{ size: size ?? 'medium' }} iconButtonProps={{ size: size ?? 'medium' }}>
      <MenuItem onClick={() => (onEdit ? onEdit(expense, costItemId) : setEntryDialog({ open: true, costItemId, type: 'expense', expense }))}>
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        {formatMessage({ id: 'Edit' })}
      </MenuItem>

      <MenuItem onClick={() => setIsDeleting(true)}>
        <ListItemIcon>
          <Delete />
        </ListItemIcon>
        {formatMessage({ id: 'Delete' })}
      </MenuItem>
    </ActionMenu>

    <ConfirmDialog
      open={isDeleting}
      onCancel={() => setIsDeleting(false)}
      onConfirm={onDelete}
      title={formatMessage({ id: 'Delete expense' })}
      confirmColor='error'
      confirmText={formatMessage({ id: 'Delete' })}
      content={formatMessage({ id: 'Are you sure you want to delete this expense?' })}
      loading={isLoading}
    />

  </>;
};